import { ChangeEvent, useCallback } from "react"

import { ArrowUpIcon } from "@chakra-ui/icons"
import { Box, Center, Heading, Spinner } from "@chakra-ui/react"
import DataTable, { SortOrder, TableColumn } from "react-data-table-component"
import { Navigate, useLocation, useNavigate } from "react-router-dom"

import { EmailCommunicationType } from "api/emailCommunication/getPaginatedEmailCommunicationApi"
import SearchInput from "components/Form/SearchInput"
import PageSelector from "components/Table/PageSelector"
import Pagination from "components/Table/Pagination"
import usePaginatedEmailLogDetails from "hooks/swr/usePaginatedEmailLogDetails"
import useDebounce from "hooks/useDebounce"
import useModulePermission from "hooks/useModulePermission"
import useRouteParameterState from "hooks/useRouteParameterState"
import { SortDirectionType, customStyles } from "services/common"
import { generateDatabaseDateTime } from "utils/misc"

import { Endpoints } from "../../types"

const SystemEmailLogPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)

  const { state, reducer } = useRouteParameterState({
    keyword: searchParams.get("keyword") || "",
    pageNo: parseInt(searchParams.get("pageNo") || "0") || 0,
    noOfRecords: parseInt(searchParams.get("noOfRecords") || "10") || 10,
    sortBy: searchParams.get("sortBy") || "created_dt",
    sortDirection:
      (searchParams.get("sortDirection") as SortDirectionType) || "DESC",
  })

  const deBouncedKeyword = useDebounce(state.keyword, 500)
  const onSort = useCallback(
    (
      selectedColumn: TableColumn<EmailCommunicationType>,
      newSortDirection: SortOrder,
    ) => {
      reducer({
        sortBy: (selectedColumn.id as string) || "created_dt",
        sortDirection: newSortDirection.toUpperCase() as SortDirectionType,
      })
    },
    [reducer],
  )

  const { data: emailCommunicationData, isLoading } =
    usePaginatedEmailLogDetails({
      ...state,
      keyword: deBouncedKeyword,
    })

  const UserDashboardColumns: TableColumn<EmailCommunicationType>[] = [
    {
      width: "200px",
      name: "Booking Number",
      cell: (row) => (
        <Box
          as='span'
          color='black.1000'
          textDecoration={row.bookingNumber ? "underline" : "none"}
          cursor='pointer'
          fontSize={"medium"}
          fontWeight={500}
          onClick={() => navigate(`/systemEmailLog/details/${row.id}`)}
          role='link'
        >
          {row.bookingNumber || "-"}
        </Box>
      ),
      sortable: true,
      id: "bookingNumber",
    },
    {
      width: "180px",
      name: "Sender",
      selector: (row) => (row.sender ? row.sender : "-"),
      id: "sender",
    },
    {
      width: "180px",
      name: "Recipient",
      selector: (row) => (row.recipient ? row.recipient : "-"),
      id: "recipient",
    },
    {
      width: "180px",
      name: "Status",
      selector: (row) => (row.status ? row.status : "-"),
      id: "Status",
    },

    {
      width: "200px",
      name: "Subject",
      selector: (row) =>
        row.subject
          ? row.subject.length > 25
            ? row.subject.slice(0, 25) + "..."
            : row.subject
          : "-",
      id: "subject",
    },
    {
      width: "180px",
      name: "Date & Time",
      selector: (row) =>
        row.createdDt !== null
          ? generateDatabaseDateTime(row.createdDt, false, true)
          : "-",
      sortable: true,
      id: "created_dt",
    },
    {
      width: "180px",
      name: "Error Message",
      selector: (row) => (row.errorMessage ? row.errorMessage : "NA"),
      id: "error_message",
    },
  ]

  const hasAccessToModule = useModulePermission("EmailCommunication")
  if (
    !hasAccessToModule.permitedActions.includes(
      "viewEmailCommunication" as never,
    )
  ) {
    return <Navigate to={Endpoints.propertyDashboard} replace />
  }

  return (
    <>
      <Box pt={2} pb={6} display='flex' flexDirection='column' flexWrap='wrap'>
        <Heading fontSize='20px' fontWeight={600} mb='20px'>
          System Email Logs
        </Heading>
        <Box display='flex' mb='20px' justifyContent='space-between'>
          <Box maxW='320px' w='full'>
            <SearchInput
              value={state.keyword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                reducer({ keyword: e.target.value, pageNo: 0 })
              }
            />
          </Box>
          {/* <ExportReportModal /> */}
        </Box>
      </Box>

      <Box border='1px solid #eee' borderRadius='12px' overflow='hidden'>
        <Box display={"grid"} className='tableBox'>
          {isLoading && (
            <Center p={8}>
              <Spinner size={"xl"} />
            </Center>
          )}
          {!isLoading && (
            <>
              <DataTable
                data={emailCommunicationData?.content || []}
                columns={UserDashboardColumns}
                highlightOnHover={true}
                sortIcon={<ArrowUpIcon />}
                striped
                onSort={onSort}
                sortServer
                defaultSortAsc={state.sortDirection === "DESC"}
                defaultSortFieldId={state.sortBy}
                customStyles={customStyles}
              />
              {emailCommunicationData?.content &&
                emailCommunicationData?.content.length > 0 && (
                  <Box p='10px' display='flex' justifyContent='flex-end'>
                    <PageSelector
                      pageSize={Number(state.noOfRecords)}
                      setPageSize={(newVal) => reducer({ noOfRecords: newVal })}
                      setPageNumber={(newVal) => reducer({ pageNo: newVal })}
                    />
                    <Pagination
                      totalPages={emailCommunicationData?.totalPages || 0}
                      pageSize={Number(state.noOfRecords)}
                      pageNumber={Number(state.pageNo)}
                      setPageNumber={(newVal) => reducer({ pageNo: newVal })}
                      totalElements={emailCommunicationData?.totalElements || 0}
                    />
                  </Box>
                )}
            </>
          )}
        </Box>
      </Box>
    </>
  )
}

export default SystemEmailLogPage
